/**
 * @file       Comments vuex store, used by the whole comments module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";
import _     from "lodash";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    comments: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        id: null,
        deliberation: null,
        user: null,
        status: null,
        flagged: null,
        public: null,
        root: null,
        discussion: null,
        type: null,
        resourcesPerPage: 20,
        page: 1
      }
    },
    comment: {
      status: null,
      data: null,
      error: null
    },
    commentGrade: {
      status: null,
      data: null,
      error: null
    },
    refGrade: {
      status: null,
      data: null,
      error: null
    },
    messages: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        comment: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    message: {
      status: null,
      data: null,
      error: null
    },
    generalMsgs: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        user: null,
        deliberation: null,
        old: null,
        resourcesPerPage: 100,
        page: 1
      }
    },
    suggestions: {
      status: null,
      data: null,
      error: null,
      filters: {
        user: null,
        deliberation: null,
        date_created: null,
        date_updated: null,
        status: null,
        config_rule: null,
        resourcesPerPage: 100,
        page: 1
      }
    },
    suggestion: {
      status: null,
      data: null,
      error: null
    },
    flowDlibr: {
      currentPositionScroll: false,
      goLastComment: false,
      goToComment: {},
      evalMode: false
    },
    activeJobs: {
      status: null,
      data: { data: [] },
      error: null,
      isActive: false,
      filters: {
        user: null,
        deliberation: null,
        type: null,
        status: null,
        resourcesPerPage: 100,
        page: 1
      }
    },
    privateMsgs: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        user: null,
        deliberation: null,
        all: null,
        resourcesPerPage: 100,
        page: 1
      }
    }
  };
}

/**
 * Comments vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get the comment.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getComment: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onGetComment");

      axios
        .get(`comment`, { params })
        .then(response => {
          commit("onGetCommentSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetCommentError", error);
          reject(error);
        });
    });
  },

  /**
   * Get comments
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getComments: ({ commit, state }) => {
    const payload = Object.entries(state.comments.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetComments");
      axios
        .get(`comment`, { params: payload })
        .then(response => {
          commit("onGetCommentsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetCommentsError", error);
          reject(error);
        });
    });
  },

  /**
   * Put the comment.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  putComment: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutComment");

      axios
        .put(`comment`, payload)
        .then(response => {
          commit("onPutCommentSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutCommentError", error);
          reject(error);
        });
    });
  },

  /**
   * Post the comment.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  postComment: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostComment");

      axios
        .post(`comment`, payload)
        .then(response => {
          commit("onPostCommentSuccess", response);
          dispatch("getComments");
          resolve(response);
        })
        .catch(error => {
          commit("onPostCommentError", error);
          reject(error);
        });
    });
  },

  /**
   * Get messages
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getMessages: ({ commit, state }) => {
    const payload = Object.entries(state.messages.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetMessages");

      axios
        .get(`comment/message`, { params: payload })
        .then(response => {
          commit("onGetMessagesSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetMessagesError", error);
          reject(error);
        });
    });
  },

  /**
   * Put message.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  putMessage: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutMessage");

      axios
        .put(`comment/message`, payload)
        .then(response => {
          commit("onPutMessageSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutMessageError", error);
          reject(error);
        });
    });
  },

  /**
   * Post message.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  postMessage: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostMessage");

      axios
        .post(`comment/message`, payload)
        .then(response => {
          commit("onPostMessageSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostMessageError", error);
          reject(error);
        });
    });
  },

  /**
   * Post comment grade.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  postCommentGrade: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postCommentGrade");

      axios
        .post(`teacher/grade/comment`, payload)
        .then(response => {
          commit("postCommentGradeSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("postCommentGradeError", error);
          reject(error);
        });
    });
  },

  /**
   * Post reference grade.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  postRefGrade: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postRefGrade");

      axios
        .post(`teacher/grade/reference`, payload)
        .then(response => {
          commit("postRefGradeSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("postRefGradeError", error);
          reject(error);
        });
    });
  },

  /**
   * Get general messages
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getGeneralMsgs: ({ commit, state }) => {
    const payload = Object.entries(state.generalMsgs.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetGeneralMsgs");

      axios
        .get(`user/generalMessage`, { params: payload })
        .then(response => {
          commit("onGetGeneralMsgsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetGeneralMsgsError", error);
          reject(error);
        });
    });
  },

  /**
   * Post general message.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  postGeneralMessage: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostGeneralMessage");

      axios
        .post(`teacher/generalMessage`, payload)
        .then(response => {
          commit("onPostGeneralMessageSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostGeneralMessageError", error);
          reject(error);
        });
    });
  },

  /**
   * Put general message.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  putGeneralMsg: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutGeneralMsgs");

      axios
        .put(`user/generalMessage`, payload)
        .then(response => {
          commit("onPutGeneralMsgsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutGeneralMsgsError", error);
          reject(error);
        });
    });
  },

  /**
   * Get suggestions
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getSuggestions: ({ commit, state }) => {
    const payload = Object.entries(state.suggestions.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetSuggestions");
      axios
        .get(`user/suggestion`, { params: payload })
        .then(response => {
          commit("onGetSuggestionsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetSuggestionsError", error);
          reject(error);
        });
    });
  },

  /**
   * Put suggestion.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    payload        The payload
   *
   * @return  {Promise}
   */
  putSuggestion: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutSuggestion");

      axios
        .put(`user/suggestion`, payload)
        .then(response => {
          commit("onPutSuggestionSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutSuggestionError", error);
          reject(error);
        });
    });
  },

  /**
   * Get active jobs
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getActiveJobs: ({ commit, state }) => {
    const payload = Object.entries(state.activeJobs.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      axios
        .get(`user/jobs`, { params: payload })
        .then(response => {
          commit("onGetActiveJobsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetActiveJobsError", error);
          reject(error);
        });
    });
  },

  /**
   * Get private messages
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getPrivateMsgs: ({ commit, state }) => {
    const payload = Object.entries(state.privateMsgs.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetPrivateMsgs");
      axios
        .get(`user/privateMessage`, { params: payload })
        .then(response => {
          commit("onGetPrivateMsgsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetPrivateMsgsError", error);
          reject(error);
        });
    });
  },

  /**
   * Update comment state
   *
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  updateStateComment: ({ dispatch, commit }, params) => {
    return new Promise((resolve, reject) => {
      dispatch("getComment", params)
        .then(response => {
          commit("onUpdateStateComment", response);
        })
        .then(response => resolve(response))
        .catch(error => {
          reject(error);
        });
    });
  }
};

/**
 * Comments vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Get comment.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetComment: state => {
    state.comment.status = "fetching";
    state.comment.data = {};
    state.comment.error = {};
  },

  /**
   * Get comment success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetCommentSuccess: (state, { data }) => {
    state.comment.status = "success";
    state.comment.error = null;
    state.comment.data = data;
  },

  /**
   * Get comment error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetCommentError: (state, err) => {
    state.comment.status = "error";
    state.comment.error = err;
  },

  /**
   * Get comments.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetComments: state => {
    state.comments.status = "fetching";
    if (!state.flowDlibr.currentPositionScroll) {
      state.comments.data = { data: [] };
    }
    state.comments.error = {};
  },

  /**
   * Get comments success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetCommentsSuccess: (state, { data }) => {
    state.comments.status = "success";
    state.comments.error = null;



    // Save old comments
    let loadedComments = null;
    if (state.comments.data.data.length) {
      loadedComments = state.comments.data.data;
    } 

    // Initialize the comments data
    state.comments.data = data;

    // Prepend to comments
    if (loadedComments !== null) {
      state.comments.data.data = state.comments.data.data.concat(loadedComments);
    }
  },

  /**
   * Create comments error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetCommentsError: (state, err) => {
    state.comments.status = "error";
    state.comments.error = err;
  },

  /**
   * Set comment filters error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  setCommentsFilters: (state, filters) => {
    if (filters.reset) {
      state.comments.filters = {
        id: null,
        deliberation: null,
        user: null,
        status: null,
        flagged: null,
        public: null,
        answer: null,
        resourcesPerPage: 20,
        page: 1
      };
    }
    if (filters.fields) {
      state.comments.filters = {
        ...state.comments.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Put comment.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutComment: state => {
    state.comment.status = "fetching";
    state.comment.error = {};
  },

  /**
   * Put comment success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutCommentSuccess: (state, { data }) => {
    state.comment.status = "success";
    state.comment.error = null;
    state.comment.data = data;

    let commentIndex = state.comments.data.data.findIndex(comment => comment.id === data.data.id);
    if (commentIndex !== -1) {
      state.comments.data.data[commentIndex] = { ...state.comments.data.data[commentIndex], ...data.data };

      // Replace the whole array so vue.js detect the change
      state.comments.data.data = _.cloneDeep(state.comments.data.data);
    }
  },

  /**
   * Put comment error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutCommentError: (state, err) => {
    state.comment.status = "error";
    state.comment.error = err;
  },
  /**
   * Post comment.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostComment: state => {
    state.comment.status = "fetching";
    state.comment.error = {};
  },

  /**
   * Post comment success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostCommentSuccess: (state, { data }) => {
    state.comment.status = "success";
    state.comment.error = null;
    state.comment.data = data;
  },

  /**
   * Post comment error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostCommentError: (state, err) => {
    state.comment.status = "error";
    state.comment.error = err;
  },

  /**
   * Get messages.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetMessages: state => {
    state.messages.status = "fetching";
    state.messages.error = {};
    state.messages.data = { data: [] };
  },

  /**
   * Get messages success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetMessagesSuccess: (state, { data }) => {
    state.messages.status = "success";
    state.messages.error = null;
    state.messages.data = data;
  },

  /**
   *Get messages error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetMessagesError: (state, err) => {
    state.messages.status = "error";
    state.messages.error = err;
  },

  /**
   *Set messages filters.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  filters   The filters
   */
  setMessagesFilters: (state, filters) => {
    if (filters.reset) {
      state.messages.filters = {
        comment: null,
        id: null,
        deliberation: null,
        user: null,
        status: null,
        flagged: null,
        public: null,
        answer: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.messages.filters = {
        ...state.messages.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Put message.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutMessage: state => {
    state.message.status = "fetching";
    state.message.error = {};
  },

  /**
   * Put message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutMessageSuccess: (state, { data }) => {
    state.message.status = "success";
    state.message.error = null;
    state.message.data = data;
  },

  /**
   * Put message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutMessageError: (state, err) => {
    state.message.status = "error";
    state.message.error = err;
  },

  /**
   * Post message.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostMessage: state => {
    state.message.status = "fetching";
    state.message.error = {};
  },

  /**
   * Post message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostMessageSuccess: (state, { data }) => {
    state.message.status = "success";
    state.message.error = null;
    state.message.data = data;
  },

  /**
   * Post message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostMessageError: (state, err) => {
    state.message.status = "error";
    state.message.error = err;
  },

  /**
   * Post comment grade.
   *
   * @param  {Object}  state   The vuex state
   */
  postCommentGrade: state => {
    state.commentGrade.status = "fetching";
    state.commentGrade.error = {};
  },

  /**
   * Post comment grade success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postCommentGradeSuccess: (state, { data }) => {
    state.commentGrade.status = "success";
    state.commentGrade.error = null;
    state.commentGrade.data = data;
  },

  /**
   * Post comment grade error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postCommentGradeError: (state, err) => {
    state.commentGrade.status = "error";
    state.commentGrade.error = err;
  },

  /**
   * Post reference grade.
   *
   * @param  {Object}  state   The vuex state
   */
  postRefGrade: state => {
    state.refGrade.status = "fetching";
    state.refGrade.error = {};
  },

  /**
   * Post reference grade success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postRefGradeSuccess: (state, { data }) => {
    state.refGrade.status = "success";
    state.refGrade.error = null;
    state.refGrade.data = data;
  },

  /**
   * Post reference grade error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postRefGradeError: (state, err) => {
    state.refGrade.status = "error";
    state.refGrade.error = err;
  },

  /**
   * Set message to edit.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  msg     The message
   */
  setMsgToEdit: (state, msg) => {
    state.message.data = msg;
  },

  /**
   * Set deliberation flow.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  flow    The flow
   */
  setFlowDlibr: (state, flow) => {
    if (flow.reset) {
      state.flowDlibr = {
        currentPositionScroll: false,
        goLastComment: false,
        goToComment: {},
        evalMode: state.flowDlibr.evalMode
      };
    }
    if (flow.fields) {
      state.flowDlibr = {
        ...state.flowDlibr,
        ...flow.fields
      };
    }
  },

  /**
   * Get general messages.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetGeneralMsgs: state => {
    state.generalMsgs.status = "fetching";
    state.generalMsgs.error = {};
  },

  /**
   * Get general messages success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetGeneralMsgsSuccess: (state, { data }) => {
    state.generalMsgs.status = "success";
    state.generalMsgs.error = null;
    state.generalMsgs.data = data;
  },

  /**
   * Gte general messages error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetGeneralMsgsError: (state, err) => {
    state.generalMsgs.status = "error";
    state.generalMsgs.error = err;
  },

  /**
   * Set general messages filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setGeneralMsgsFilters: (state, filters) => {
    if (filters.reset) {
      state.generalMsgs.filters = {
        user: null,
        deliberation: null,
        old: null,
        resourcesPerPage: 100,
        page: 1
      };
    }
    if (filters.fields) {
      state.generalMsgs.filters = {
        ...state.generalMsgs.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Post general message.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostGeneralMessage: state => {
    state.message.status = "fetching";
    state.message.error = {};
  },

  /**
   * Post general message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostGeneralMessageSuccess: (state, { data }) => {
    state.message.status = "success";
    state.message.error = null;
    state.message.data = data;
  },

  /**
   * Post general message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostGeneralMessageError: (state, err) => {
    state.message.status = "error";
    state.message.error = err;
  },

  /**
   * Put general messages.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutGeneralMsgs: state => {
    state.message.status = "fetching";
    state.message.error = {};
  },

  /**
   * Put general message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutGeneralMsgsSuccess: (state, { data }) => {
    state.message.status = "success";
    state.message.error = null;
    state.message.data = data;
  },

  /**
   * Put general messages error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutGeneralMsgsError: (state, err) => {
    state.message.status = "error";
    state.message.error = err;
  },

  /**
   * Get suggestions.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetSuggestions: state => {
    state.suggestions.status = "fetching";
    state.suggestions.data = { data: [] };
    state.suggestions.error = {};
  },

  /**
   * Get suggestions success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetSuggestionsSuccess: (state, { data }) => {
    state.suggestions.status = "success";
    state.suggestions.error = null;
    state.suggestions.data = data;
  },

  /**
   * Get suggestions error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetSuggestionsError: (state, err) => {
    state.suggestions.status = "error";
    state.suggestions.error = err;
  },

  /**
   * Set suggestions filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setSuggestionsFilters: (state, filters) => {
    if (filters.reset) {
      state.suggestions.filters = {
        user: null,
        deliberation: null,
        date_created: null,
        date_updated: null,
        status: null,
        config_rule: null,
        resourcesPerPage: 100,
        page: 1
      };
    }
    if (filters.fields) {
      state.suggestions.filters = {
        ...state.suggestions.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Put suggestion.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutSuggestion: state => {
    state.suggestion.status = "fetching";
    state.suggestion.error = {};
  },

  /**
   * Put suggestion success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutSuggestionSuccess: (state, { data }) => {
    state.suggestion.status = "success";
    state.suggestion.error = null;
    state.suggestion.data = data;
  },

  /**
   * Put suggestion error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutSuggestionError: (state, err) => {
    state.suggestion.status = "error";
    state.suggestion.error = err;
  },

  /**
   * Get active jobs.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetActiveJobs: state => {
    state.activeJobs.status = "fetching";
    state.activeJobs.error = {};
    state.activeJobs.data = { data: [] };
  },

  /**
   * Get active jobs success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetActiveJobsSuccess: (state, { data }) => {
    state.activeJobs.status = "success";
    state.activeJobs.error = null;
    state.activeJobs.data = data;
    state.activeJobs.isActive = true;
  },

  /**
   * Get active jobs error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetActiveJobsError: (state, err) => {
    state.activeJobs.status = "error";
    state.activeJobs.error = err;
  },

  /**
   * Set active jobs filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setActiveJobsFilters: (state, filters) => {
    if (filters.reset) {
      state.activeJobs.filters = {
        user: null,
        deliberation: null,
        status: null,
        type: null,
        resourcesPerPage: 100,
        page: 1
      };
    }
    if (filters.fields) {
      state.activeJobs.filters = {
        ...state.activeJobs.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Get private messages.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetPrivateMsgs: state => {
    state.privateMsgs.status = "fetching";
    state.privateMsgs.error = {};
    state.privateMsgs.data = { data: [] };
  },

  /**
   * Get provate messages success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetPrivateMsgsSuccess: (state, { data }) => {
    state.privateMsgs.status = "success";
    state.privateMsgs.error = null;
    state.privateMsgs.data = data;
  },

  /**
   * Get private messages error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetPrivateMsgsError: (state, err) => {
    state.privateMsgs.status = "error";
    state.privateMsgs.error = err;
  },

  /**
   * Set private messages filters.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  filters   The filters
   */
  setPrivateMsgsFilters: (state, filters) => {
    if (filters.reset) {
      state.privateMsgs.filters = {
        user: null,
        deliberation: null,
        all: null,
        resourcesPerPage: 100,
        page: 1
      };
    }
    if (filters.fields) {
      state.privateMsgs.filters = {
        ...state.privateMsgs.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Reset general messages.
   *
   * @param  {Object}  state     The vuex state
   */
  resetGeneralMessages: state => {
    state.generalMsgs.data = { data: [] };
  },

  /**
   * Set active jobs.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Bool}    value     Active (true) or not (false)
   */
  setActiveJobs: (state, value) => {
    state.activeJobs.isActive = value;
  },

  /**
   * Update comment state.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onUpdateStateComment: (state, { data }) => {
    state.comments.data.data = state.comments.data.data.map(obj => {
      return obj.id == data.results.id ? data.results : obj;
    });
  },
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
